import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { emailTemplatesApi } from "./api/emailTemplatesApi";

function stripHtml(html) {
  return String(html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function TemplateLibrary({
  open,
  onClose,
  onApply,
  currentSubject,
  currentBodyHtml,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);

  // Save-as dialog
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Delete confirm
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await emailTemplatesApi.list();
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadTemplates();
      setSearch("");
      setSelectedId(null);
      setSelectedTemplate(null);
    }
  }, [open, loadTemplates]);

  const filtered = templates.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || (t.subject || "").toLowerCase().includes(q);
  });

  const handleSelect = async (template) => {
    if (selectedId === template.id) {
      setSelectedId(null);
      setSelectedTemplate(null);
      return;
    }
    setSelectedId(template.id);
    setLoadingFull(true);
    try {
      const full = await emailTemplatesApi.get(template.id);
      setSelectedTemplate(full);
    } catch {
      setSelectedTemplate(template);
    } finally {
      setLoadingFull(false);
    }
  };

  const handleApply = () => {
    if (!selectedTemplate) return;
    onApply({
      subject: selectedTemplate.subject || "",
      body_html: selectedTemplate.body_html || "",
    });
    onClose();
  };

  const handleSave = async () => {
    if (!saveName.trim()) {
      setSaveError("Please enter a template name.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const created = await emailTemplatesApi.create({
        name: saveName.trim(),
        subject: currentSubject || "",
        body_html: currentBodyHtml || "",
      });
      setTemplates((prev) => [created, ...prev]);
      setSaveOpen(false);
      setSaveName("");
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await emailTemplatesApi.delete(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      if (selectedId === id) {
        setSelectedId(null);
        setSelectedTemplate(null);
      }
    } catch {
      // ignore
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const hasContent = Boolean(currentSubject || currentBodyHtml);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <BookmarkBorderIcon sx={{ color: "#6B7280", fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={900} sx={{ flex: 1 }}>
            Template Library
          </Typography>
          <Tooltip title={hasContent ? "" : "Write a subject or message first"} arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!hasContent}
                onClick={() => { setSaveName(""); setSaveError(""); setSaveOpen(true); }}
                sx={{ textTransform: "none", fontWeight: 800, whiteSpace: "nowrap" }}
              >
                Save current
              </Button>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Search */}
        <Box sx={{ px: 2.5, pt: 1.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name or subject…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Template list */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {loading ? (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
          ) : filtered.length === 0 ? (
            <Stack spacing={1} alignItems="center" sx={{ p: 4, textAlign: "center" }}>
              <BookmarkBorderIcon sx={{ color: "#D1D5DB", fontSize: 40 }} />
              <Typography variant="body2" color="text.secondary">
                {search
                  ? 'No templates match your search.'
                  : 'No templates yet. Write a message and click “Save current” to create your first template.'}
              </Typography>
            </Stack>
          ) : (
            <List disablePadding>
              {filtered.map((t) => (
                <React.Fragment key={t.id}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={(e) => { e.stopPropagation(); setDeleteId(t.id); }}
                          sx={{ color: "#D1D5DB", "&:hover": { color: "#EF4444" } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemButton
                      selected={selectedId === t.id}
                      onClick={() => handleSelect(t)}
                      sx={{ pr: 6, py: 1.25 }}
                    >
                      <ListItemText
                        primary={t.name}
                        secondary={t.subject || "No subject"}
                        primaryTypographyProps={{
                          fontWeight: selectedId === t.id ? 900 : 600,
                          fontSize: "0.875rem",
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.75rem",
                          noWrap: true,
                          sx: { color: "#9CA3AF" },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>

                  {selectedId === t.id && (
                    <Box
                      sx={{
                        bgcolor: "#F9FAFB",
                        px: 2.5,
                        py: 1.5,
                        borderTop: "1px solid #E5E7EB",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      {loadingFull ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Stack spacing={1}>
                          {selectedTemplate?.subject ? (
                            <Typography variant="caption" color="text.secondary">
                              <strong>Subject:</strong> {selectedTemplate.subject}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              whiteSpace: "pre-wrap",
                              maxHeight: 80,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {stripHtml(selectedTemplate?.body_html).slice(0, 200) || "No content preview."}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleApply}
                            sx={{ textTransform: "none", fontWeight: 800, alignSelf: "flex-start" }}
                          >
                            Use this template
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  )}

                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Save as template dialog */}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth sx={{ zIndex: 1500 }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Save as template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Template name"
            fullWidth
            size="small"
            value={saveName}
            onChange={(e) => { setSaveName(e.target.value); setSaveError(""); }}
            placeholder="e.g. Monthly newsletter, Promo blast"
            sx={{ mt: 1 }}
          />
          {saveError ? <Alert severity="error" sx={{ mt: 1 }}>{saveError}</Alert> : null}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Saves the current subject line and message body.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSaveOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {saving ? "Saving…" : "Save template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth sx={{ zIndex: 1500 }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove the template and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDelete(deleteId)}
            disabled={deleting}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
